import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'scratch-vm';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import io from 'socket.io-client';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {openExtensionLibrary} from '../reducers/modals';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    openUploading,
    closeUploading,
    updateModalUploadType,
    openUploadFailure,
    openUploadSuccess,
    openSerialConnectionModal
} from '../reducers/modals';

import {updateStatus} from '../reducers/connection';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {config} from '../config';

import Blockly from 'scratch-blocks';

const messages = defineMessages({
    defaultProjectTitle: {
        id: 'gui.gui.defaultProjectTitle',
        description: 'Default title for project',
        defaultMessage: 'Scratch Project'
    }
});

class GUI extends React.Component {
    constructor (props) {
        super(props);
        this.socket = io(config.socketUrl);
        bindAll(this, [
            'burningToDevice'
        ]);
    }
    componentDidMount () {
        this.setReduxTitle(this.props.projectTitle);
        this.props.onStorageInit(storage);
        // 刷新时提示
        window.onbeforeunload = () => 'Are you sure you want to leave?';
        // 页面加载完立即初始化
        window.onload = () => {
            this.socket.emit('serialDisconnect', () => {});
        };
    }
    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId && this.props.projectId !== null) {
            this.props.onUpdateProjectId(this.props.projectId);
        }
        if (this.props.projectTitle !== prevProps.projectTitle) {
            this.setReduxTitle(this.props.projectTitle);
        }
    }
    setReduxTitle (newTitle) {
        if (newTitle === null || typeof newTitle === 'undefined') {
            this.props.onUpdateReduxProjectTitle(
                this.props.intl.formatMessage(messages.defaultProjectTitle)
            );
        } else {
            this.props.onUpdateReduxProjectTitle(newTitle);
        }
    }

    burningToDevice (e) {
        e.stopPropagation();
        const {vm} = this.props;
        let arduinoCode = '';
        if (vm.editingTarget) {
            let _iteratorNormalCompletion = true;
            let _didIteratorError = false;
            let _iteratorError = null;
            // eslint-disable-next-line newline-per-chained-call
            const _iterator = Blockly.getMainWorkspace().getTopBlocks(true)[Symbol.iterator]();
            let _step;
            try {
                for (;!(_iteratorNormalCompletion = (_step = _iterator.next()).done);
                    _iteratorNormalCompletion = true) {
                    const item = _step.value;
                    if (item.type !== 'event_whenprogrambegin') {
                        continue;
                    }
                    let b = [];
                    const arduino = Blockly.Arduino;
                    arduino.init(Blockly.getMainWorkspace());
                    const d = item;
                    let e;
                    try {
                        e = arduino.blockToCode(d);
                    } catch (error) {
                        return;
                    }
                    // eslint-disable-next-line no-unused-expressions
                    Array.isArray(e) && (e = e[0]);
                    if (e) {
                        if (d.outputConnection && arduino.scrubNakedValue) {
                            e = arduino.scrubNakedValue(e);
                        }
                        b.push(e);
                    }
                    b = b.join('\n');
                    b = arduino.finish(b);
                    b = b.replace(/^\s+\n/, '');
                    b = b.replace(/\n\s+$/, '\n');
                    arduinoCode = b.replace(/[ \t]+\n/g, '\n');
                    break;
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally {
                try {
                    /* eslint-disable no-undef */
                    if (!_iteratorNormalCompletion && _iterator.return) {
                        _iterator.return();
                    }
                } finally {
                    if (_didIteratorError) {
                        // eslint-disable-next-line no-unsafe-finally
                        throw _iteratorError;
                    }
                }
            }
            
            if (arduinoCode.length === 0) {
                arduinoCode = 'void setup() {\n\n}\n\nvoid loop() {\n\n}';
            }
        }
        if (!this.props.status) {
            this.props.onOpenConnection();
            return;
        }
        let board = '';
        const version = parseInt(this.props.version, 10);
        const selectedItem_ = Blockly.mainWorkspace.toolbox_.selectedItem_.id_;

        if (version === 1 || selectedItem_ === 'qoopers') {
            board = 'mega';
        } else if (version === 2 || selectedItem_ === 'qscout') {
            board = 'uno';
        } else {
            return;
        }
        this.props.onUpdateModalUploadType('burning');
        this.props.onOpenUploading();
        const data = {
            board,
            url: config.getHexUrl(board === 'mega' ? 'k1' : 'k2'),
            code: arduinoCode
        };

        this.props.onUpdateStatus();
        this.socket.emit('compile', data, res => {
            this.props.onCloseUploading();
            if (res) {
                this.props.onOpenUploadSuccessModal();
            } else {
                this.props.onOpenUploadFailureModal();
            }
            this.props.onUpdateStatus(false);
        });
    }

    
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isShowingProject,
            onStorageInit,
            onUpdateProjectId,
            onUpdateReduxProjectTitle,
            projectHost,
            projectId,
            projectTitle,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                burningToDevice={this.burningToDevice}
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    importInfoVisible: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onOpenUploadFailureModal: PropTypes.func,
    onOpenUploadSuccessModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateModalUploadType: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onUpdateProjectTitle: PropTypes.func,
    onUpdateReduxProjectTitle: PropTypes.func,
    onUpdateStatus: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    projectTitle: PropTypes.string,
    status: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUI.defaultProps = {
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onUpdateProjectId: () => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        importInfoVisible: state.scratchGui.modals.importInfo,
        isError: getIsError(loadingState),
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,
        version: state.scratchGui.connection.version,
        editingTarget: state.scratchGui.targets,
        status: state.scratchGui.connection.status
    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onUpdateReduxProjectTitle: title => dispatch(setProjectTitle(title)),
    onOpenUploading: () => dispatch(openUploading()),
    onCloseUploading: () => dispatch(closeUploading()),
    onUpdateModalUploadType: type => dispatch(updateModalUploadType(type)),
    onUpdateStatus: () => dispatch(updateStatus()),
    onOpenUploadSuccessModal: () => {
        dispatch(openUploadSuccess());
    },
    onOpenUploadFailureModal: () => {
        dispatch(openUploadFailure());
    },
    onOpenConnection: () => dispatch(openSerialConnectionModal())
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    cloudManagerHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
